import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Sparkles, 
  Clock, 
  Target, 
  BookOpen, 
  Printer,
  Loader2,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Users,
  Package,
  Wand2,
  Save,
  FolderOpen,
  FileText,
  ClipboardList,
  ClipboardCheck,
  CheckCircle,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = '';

const AILessonPlanGenerator = ({ defaultYear = 1, defaultSubject = 'english', schoolType = 'primary', allowedYears = null, allowedSubjects = null }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate');
  
  // Lesson generation state
  const [topic, setTopic] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(defaultSubject || 'english');
  // Parse defaultYear - it might be "Year 4" or just 4
  const parsedDefaultYear = typeof defaultYear === 'string' 
    ? parseInt(defaultYear.replace('Year ', '')) || 1 
    : (defaultYear || 1);
  const [selectedYear, setSelectedYear] = useState(parsedDefaultYear.toString());
  const [duration, setDuration] = useState('45');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [learningObjectives, setLearningObjectives] = useState('');
  const [studentAbilities, setStudentAbilities] = useState('mixed');
  const [availableResources, setAvailableResources] = useState('');
  const [teachingStyle, setTeachingStyle] = useState('interactive');
  const [loading, setLoading] = useState(false);
  const [generatedLesson, setGeneratedLesson] = useState(null);
  const [generatingWeek, setGeneratingWeek] = useState(false);
  const [weekLessons, setWeekLessons] = useState(null);
  
  // Differentiation state
  const [differentiating, setDifferentiating] = useState(false);
  const [differentiatedLessons, setDifferentiatedLessons] = useState(null);
  const [selectedDiffLevel, setSelectedDiffLevel] = useState('standard');
  
  // Assessment generation state
  const [assessTopic, setAssessTopic] = useState('');
  const [assessType, setAssessType] = useState('quiz');
  const [difficulty, setDifficulty] = useState('medium');
  const [numQuestions, setNumQuestions] = useState('10');
  const [questionTypes, setQuestionTypes] = useState(['multiple_choice']);
  const [generatingAssessment, setGeneratingAssessment] = useState(false);
  const [generatedAssessment, setGeneratedAssessment] = useState(null);
  const [showAnswerKey, setShowAnswerKey] = useState(false);
  
  // Exam generation state
  const [examTerm, setExamTerm] = useState('1');
  const [examTotalMarks, setExamTotalMarks] = useState('100');
  const [examDuration, setExamDuration] = useState('120');
  const [examTopics, setExamTopics] = useState('');
  const [generatingExam, setGeneratingExam] = useState(false);
  const [generatedExam, setGeneratedExam] = useState(null);
  const [showExamAnswerKey, setShowExamAnswerKey] = useState(false);
  
  // Library state
  const [myLessons, setMyLessons] = useState([]);
  const [myAssessments, setMyAssessments] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [savingLesson, setSavingLesson] = useState(false);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [loadingAssessmentId, setLoadingAssessmentId] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Subject options based on school type
  const allSubjectOptions = schoolType === 'primary' ? [
    { value: 'english', label: 'Language & Communication' },
    { value: 'mathematics', label: 'Mathematics' },
    { value: 'science', label: 'Science' }
  ] : [
    { value: 'ict', label: 'ICT / Computer Studies' },
    { value: 'chemistry', label: 'Chemistry' },
    { value: 'economics', label: 'Economics' },
    { value: 'physics', label: 'Physics' },
    { value: 'biology', label: 'Biology' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'english', label: 'English' },
    { value: 'mathematics', label: 'Mathematics' },
    { value: 'science', label: 'Science' },
    { value: 'french', label: 'French' },
    { value: 'geography', label: 'Geography' }
  ];
  
  // Filter subjects if allowedSubjects is provided
  const subjectOptions = allowedSubjects && allowedSubjects.length > 0
    ? allSubjectOptions.filter(s => allowedSubjects.includes(s.value))
    : allSubjectOptions;

  // Year options based on school type and allowed years
  const yearOptions = allowedYears && allowedYears.length > 0
    ? allowedYears.map(y => typeof y === 'string' ? parseInt(y.replace('Year ', '')) : y)
    : schoolType === 'primary' 
      ? [1, 2, 3, 4, 5, 6]
      : [7, 8, 9, 10, 11, 12, 13];

  const questionTypeOptions = [
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'true_false', label: 'True/False' },
    { value: 'fill_blank', label: 'Fill in the Blank' },
    { value: 'short_answer', label: 'Short Answer' },
    { value: 'matching', label: 'Matching' }
  ];

  // Fetch library when tab changes
  useEffect(() => {
    if (activeTab === 'library' && user?.id) {
      fetchLibrary();
    }
  }, [activeTab]);

  const fetchLibrary = async () => {
    setLoadingLibrary(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const [lessonsRes, assessmentsRes] = await Promise.all([
        fetch(`${API_URL}/api/ai-lessons/my-lessons?teacher_id=${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/ai-lessons/my-assessments?teacher_id=${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (lessonsRes.ok) setMyLessons(await lessonsRes.json());
      if (assessmentsRes.ok) setMyAssessments(await assessmentsRes.json());
    } catch (error) {
      console.error('Error fetching library:', error);
    } finally {
      setLoadingLibrary(false);
    }
  };

  // Load a saved assessment from library
  const loadAssessmentFromLibrary = async (assessmentId) => {
    setLoadingAssessmentId(assessmentId);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/ai-lessons/assessment/${assessmentId}?include_answers=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const assessment = await response.json();
        // Set the assessment data to display it
        setGeneratedAssessment({
          title: assessment.title,
          questions: assessment.questions,
          answer_key: assessment.answer_key,
          total_marks: assessment.total_marks,
          time_limit: assessment.time_limit,
          instructions: assessment.instructions
        });
        // Switch to assessment tab to show it
        setActiveTab('assessment');
        // Pre-fill the form fields with the assessment's metadata
        setAssessTopic(assessment.topic || '');
        setAssessType(assessment.assessment_type || 'quiz');
        setDifficulty(assessment.difficulty || 'medium');
        setSelectedSubject(assessment.subject || selectedSubject);
        setSelectedYear(assessment.year_level?.toString() || selectedYear);
        toast.success('Assessment loaded from library!');
      } else {
        toast.error('Failed to load assessment');
      }
    } catch (error) {
      console.error('Error loading assessment:', error);
      toast.error('Failed to load assessment');
    } finally {
      setLoadingAssessmentId(null);
    }
  };

  const generateSingleLesson = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    const yearLevel = parseInt(selectedYear) || (schoolType === 'primary' ? 1 : 7);
    setLoading(true);
    setGeneratedLesson(null);
    setWeekLessons(null);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/ai-lessons/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          topic: topic.trim(),
          subject: selectedSubject || 'english',
          year_level: yearLevel,
          duration: parseInt(duration) || 45,
          school_type: schoolType,
          learning_objectives: learningObjectives ? learningObjectives.split('\n').filter(o => o.trim()) : null,
          student_abilities: studentAbilities,
          available_resources: availableResources || null,
          teaching_style: teachingStyle
        })
      });

      const data = await response.json();
      if (data.success) {
        setGeneratedLesson(data.lesson);
        setDifferentiatedLessons(null); // Reset differentiation when new lesson generated
        toast.success('Lesson plan generated!');
      } else {
        toast.error(data.detail || 'Failed to generate lesson');
      }
    } catch (error) {
      toast.error('Failed to generate lesson plan');
    } finally {
      setLoading(false);
    }
  };

  const differentiateLesson = async () => {
    if (!generatedLesson) {
      toast.error('Generate a lesson first');
      return;
    }

    setDifferentiating(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/ai-lessons/differentiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          lesson: generatedLesson,
          topic: topic.trim(),
          subject: selectedSubject || 'english',
          year_level: parseInt(selectedYear) || 1,
          school_type: schoolType
        })
      });

      const data = await response.json();
      if (data.success) {
        setDifferentiatedLessons(data.differentiated_lessons);
        toast.success('Created 3 differentiated versions!');
      } else {
        toast.error(data.detail || 'Failed to differentiate lesson');
      }
    } catch (error) {
      toast.error('Failed to create differentiated versions');
    } finally {
      setDifferentiating(false);
    }
  };

  const generateWeekPlan = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    const yearLevel = parseInt(selectedYear) || (schoolType === 'primary' ? 1 : 7);
    setGeneratingWeek(true);
    setGeneratedLesson(null);
    setWeekLessons(null);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/ai-lessons/generate-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          topic: topic.trim(),
          subject: selectedSubject || 'english',
          year_level: yearLevel,
          duration: parseInt(duration) || 45,
          school_type: schoolType
        })
      });

      const data = await response.json();
      if (data.success) {
        setWeekLessons(data.lessons);
        toast.success('Week plan generated!');
      } else {
        toast.error(data.detail || 'Failed to generate week plan');
      }
    } catch (error) {
      toast.error('Failed to generate week plan');
    } finally {
      setGeneratingWeek(false);
    }
  };

  const generateAssessment = async () => {
    if (!assessTopic.trim()) {
      toast.error('Please enter a topic for the assessment');
      return;
    }

    if (questionTypes.length === 0) {
      toast.error('Please select at least one question type');
      return;
    }

    const yearLevel = parseInt(selectedYear) || (schoolType === 'primary' ? 1 : 7);
    setGeneratingAssessment(true);
    setGeneratedAssessment(null);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/ai-lessons/generate-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          topic: assessTopic.trim(),
          subject: selectedSubject || 'english',
          year_level: yearLevel,
          assessment_type: assessType,
          difficulty: difficulty,
          num_questions: parseInt(numQuestions) || 10,
          question_types: questionTypes,
          school_type: schoolType,
          include_answer_key: true
        })
      });

      const data = await response.json();
      if (data.success) {
        setGeneratedAssessment(data.assessment);
        toast.success('Assessment generated!');
      } else {
        toast.error(data.detail || 'Failed to generate assessment');
      }
    } catch (error) {
      toast.error('Failed to generate assessment');
    } finally {
      setGeneratingAssessment(false);
    }
  };

  const saveLesson = async (lesson, isWeekPlan = false) => {
    setSavingLesson(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/ai-lessons/save-lesson?teacher_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: isWeekPlan ? `Week Plan: ${topic}` : lesson.title,
          subject: selectedSubject,
          year_level: parseInt(selectedYear) || 1,
          topic: topic,
          duration: parseInt(duration),
          objectives: lesson.objectives,
          introduction: lesson.introduction,
          activities: lesson.activities,
          materials: lesson.materials,
          assessment: lesson.assessment,
          homework: lesson.homework,
          is_week_plan: isWeekPlan,
          week_lessons: isWeekPlan ? weekLessons : null
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Saved to your library!');
      } else {
        toast.error('Failed to save lesson');
      }
    } catch (error) {
      toast.error('Failed to save lesson');
    } finally {
      setSavingLesson(false);
    }
  };

  const saveAssessment = async () => {
    if (!generatedAssessment) return;
    
    setSavingAssessment(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/ai-lessons/save-assessment?teacher_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: generatedAssessment.title,
          subject: selectedSubject,
          year_level: parseInt(selectedYear) || 1,
          topic: assessTopic,
          assessment_type: assessType,
          difficulty: difficulty,
          questions: generatedAssessment.questions,
          answer_key: generatedAssessment.answer_key,
          total_marks: generatedAssessment.total_marks,
          time_limit: generatedAssessment.time_limit,
          instructions: generatedAssessment.instructions
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Assessment saved to your library!');
      } else {
        toast.error('Failed to save assessment');
      }
    } catch (error) {
      toast.error('Failed to save assessment');
    } finally {
      setSavingAssessment(false);
    }
  };

  // Generate End-of-Term Exam
  const generateExam = async () => {
    if (!examTopics.trim()) {
      toast.error('Please enter the topics to be covered in the exam');
      return;
    }

    setGeneratingExam(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const subjectLabel = subjectOptions.find(s => s.value === selectedSubject)?.label || selectedSubject;
      
      const response = await fetch(`${API_URL}/api/ai-lessons/generate-exam`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subject: subjectLabel,
          year_level: parseInt(selectedYear) || 1,
          term: parseInt(examTerm),
          total_marks: parseInt(examTotalMarks),
          duration_minutes: parseInt(examDuration),
          topics: examTopics,
          school_type: schoolType
        })
      });

      const data = await response.json();
      if (data.exam) {
        setGeneratedExam(data.exam);
        toast.success('Term exam generated successfully!');
      } else {
        toast.error(data.detail || data.error || 'Failed to generate exam');
      }
    } catch (error) {
      console.error('Error generating exam:', error);
      toast.error('Failed to generate exam. The AI service may be busy. Please try again.');
    } finally {
      setGeneratingExam(false);
    }
  };

  // Save exam to grade book with max marks
  const saveExamToGradeBook = () => {
    if (!generatedExam) return;
    
    const examData = {
      type: 'exam',
      title: generatedExam.title || `Term ${examTerm} Examination`,
      subject: selectedSubject,
      year: parseInt(selectedYear),
      term: examTerm,
      totalMarks: generatedExam.total_marks,
      durationMinutes: generatedExam.duration_minutes,
      sections: generatedExam.sections,
      createdAt: new Date().toISOString(),
      source: 'ai-generator'
    };
    
    // Store in localStorage for the grade book to pick up
    localStorage.setItem('pendingExamForGradeBook', JSON.stringify(examData));
    
    toast.success(`Exam saved! Max marks set to ${generatedExam.total_marks}`, {
      description: 'Redirecting to Grade Book for batch entry...'
    });
    
    // Navigate to grade book
    setTimeout(() => {
      navigate('/dashboard/gradebook', { 
        state: { 
          examFromGenerator: examData,
          setMaxMarks: generatedExam.total_marks 
        } 
      });
    }, 1000);
  };

  // Print exam paper
  const printExam = (showAnswers = false) => {
    if (!generatedExam) return;
    
    const subjectLabel = subjectOptions.find(s => s.value === selectedSubject)?.label || selectedSubject;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Term ${examTerm} Examination - Year ${selectedYear} ${subjectLabel}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Times New Roman', serif; padding: 20px; line-height: 1.5; font-size: 12pt; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 15px; }
          .header h1 { font-size: 18pt; margin-bottom: 5px; }
          .header h2 { font-size: 14pt; color: #333; margin-bottom: 10px; }
          .header-info { display: flex; justify-content: space-between; margin-top: 10px; font-size: 11pt; }
          .student-info { margin: 15px 0; padding: 10px; border: 1px solid #333; }
          .student-info table { width: 100%; }
          .student-info td { padding: 5px; border-bottom: 1px dotted #999; }
          .student-info td:first-child { width: 120px; font-weight: bold; }
          .instructions { margin: 15px 0; padding: 10px; background: #f5f5f5; border-radius: 5px; }
          .instructions h3 { margin-bottom: 8px; }
          .instructions ul { margin-left: 20px; }
          .section { margin: 20px 0; page-break-inside: avoid; }
          .section-header { background: #333; color: white; padding: 8px 12px; font-weight: bold; margin-bottom: 10px; }
          .question { margin: 15px 0; padding: 10px 0; border-bottom: 1px solid #eee; }
          .question-num { font-weight: bold; }
          .marks { float: right; font-weight: bold; color: #666; }
          .answer-space { margin: 10px 0; min-height: 60px; border: 1px dashed #ccc; padding: 5px; }
          .options { margin: 10px 0 10px 20px; }
          .option { margin: 5px 0; }
          .option-box { display: inline-block; width: 15px; height: 15px; border: 1px solid #333; margin-right: 8px; vertical-align: middle; }
          .answer-key { margin-top: 30px; padding: 20px; background: #e8f5e9; border: 2px solid #4caf50; }
          .answer-key h3 { color: #2e7d32; margin-bottom: 15px; }
          .answer-item { margin: 5px 0; padding: 5px; border-bottom: 1px solid #c8e6c9; }
          .mark-sheet { margin-top: 30px; page-break-before: always; }
          .mark-sheet h3 { margin-bottom: 15px; text-align: center; }
          .mark-table { width: 100%; border-collapse: collapse; }
          .mark-table th, .mark-table td { border: 1px solid #333; padding: 8px; text-align: center; }
          .mark-table th { background: #f0f0f0; }
          .mark-table .section-row { background: #e3f2fd; font-weight: bold; }
          @media print {
            .page-break { page-break-before: always; }
            body { padding: 10mm; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PENAMA PROVINCE EDUCATION</h1>
          <h2>Year ${selectedYear} ${subjectLabel} - Term ${examTerm} Examination ${new Date().getFullYear()}</h2>
          <div class="header-info">
            <span><strong>Total Marks:</strong> ${generatedExam.total_marks}</span>
            <span><strong>Duration:</strong> ${generatedExam.duration_minutes} minutes</span>
          </div>
        </div>

        <div class="student-info">
          <table>
            <tr><td>Student Name:</td><td>_________________________________________________</td></tr>
            <tr><td>Class:</td><td>____________</td><td style="width:100px">Date:</td><td>_______________</td></tr>
          </table>
        </div>

        <div class="instructions">
          <h3>Instructions:</h3>
          <ul>
            ${generatedExam.instructions?.map(inst => `<li>${inst}</li>`).join('') || '<li>Read all questions carefully before answering.</li><li>Answer all questions.</li><li>Write your answers clearly in the spaces provided.</li>'}
          </ul>
        </div>

        ${generatedExam.sections?.map((section, sIdx) => `
          <div class="section">
            <div class="section-header">
              Section ${String.fromCharCode(65 + sIdx)}: ${section.title} (${section.marks} marks)
            </div>
            ${section.questions?.map((q, qIdx) => `
              <div class="question">
                <span class="question-num">${qIdx + 1}.</span>
                <span class="marks">[${q.marks} marks]</span>
                <p style="margin-top:5px">${q.question}</p>
                ${q.options ? `
                  <div class="options">
                    ${q.options.map((opt, oIdx) => `
                      <div class="option">
                        <span class="option-box"></span>
                        ${String.fromCharCode(65 + oIdx)}. ${opt}
                      </div>
                    `).join('')}
                  </div>
                ` : `
                  <div class="answer-space"></div>
                `}
              </div>
            `).join('')}
          </div>
        `).join('')}

        ${showAnswers ? `
          <div class="answer-key page-break">
            <h3>🔑 ANSWER KEY - Term ${examTerm} Examination</h3>
            ${generatedExam.sections?.map((section, sIdx) => `
              <div style="margin: 15px 0;">
                <strong>Section ${String.fromCharCode(65 + sIdx)}: ${section.title}</strong>
                ${section.questions?.map((q, qIdx) => `
                  <div class="answer-item">
                    <strong>Q${qIdx + 1}:</strong> ${q.answer || 'See marking scheme'}
                    ${q.marking_notes ? `<br><em style="color:#666">Marking notes: ${q.marking_notes}</em>` : ''}
                  </div>
                `).join('')}
              </div>
            `).join('')}
          </div>
        ` : ''}

        <div class="mark-sheet ${showAnswers ? '' : 'page-break'}">
          <h3>📊 MARK SHEET - For Teacher Use Only</h3>
          <table class="mark-table">
            <thead>
              <tr>
                <th>Section</th>
                <th>Question</th>
                <th>Max Marks</th>
                <th>Marks Obtained</th>
              </tr>
            </thead>
            <tbody>
              ${generatedExam.sections?.map((section, sIdx) => `
                <tr class="section-row">
                  <td colspan="4">Section ${String.fromCharCode(65 + sIdx)}: ${section.title} (${section.marks} marks)</td>
                </tr>
                ${section.questions?.map((q, qIdx) => `
                  <tr>
                    <td></td>
                    <td>Q${qIdx + 1}</td>
                    <td>${q.marks}</td>
                    <td></td>
                  </tr>
                `).join('')}
              `).join('')}
              <tr style="font-weight: bold; background: #ffeb3b;">
                <td colspan="2">TOTAL</td>
                <td>${generatedExam.total_marks}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <div style="margin-top: 20px;">
            <p><strong>Teacher's Signature:</strong> __________________ <strong>Date:</strong> __________</p>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const toggleQuestionType = (type) => {
    setQuestionTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const printLesson = (lesson, isWeek = false) => {
    // Same print logic as before...
    const printWindow = window.open('', '_blank');
    const subjectLabel = subjectOptions.find(s => s.value === selectedSubject)?.label || selectedSubject;
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>AI Lesson Plan - Year ${selectedYear} ${subjectLabel}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, #f59e0b, #d97706); color: white; padding: 20px; border-radius: 12px; margin-bottom: 20px; }
          .header h1 { margin: 0; }
          .lesson-section { margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
          .lesson-section h3 { margin-top: 0; color: #d97706; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f3f4f6; }
          .footer { text-align: center; margin-top: 30px; padding: 15px; background: #f8fafc; border-radius: 8px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>✨ AI-Generated Lesson Plan</h1>
          <p>Year ${selectedYear} ${subjectLabel} | Topic: ${topic}</p>
        </div>
        ${isWeek && weekLessons ? weekLessons.map(l => `
          <div class="lesson-section">
            <h3>${l.day}: ${l.title}</h3>
            <p><strong>Objectives:</strong> ${l.objectives?.join(', ') || ''}</p>
            <p><strong>Introduction:</strong> ${l.introduction || ''}</p>
            <p><strong>Materials:</strong> ${l.materials || ''}</p>
            <p><strong>Homework:</strong> ${l.homework || ''}</p>
          </div>
        `).join('') : `
          <div class="lesson-section">
            <h3>${lesson.title}</h3>
            <p><strong>Objectives:</strong> ${lesson.objectives?.join(', ') || ''}</p>
            <p><strong>Introduction:</strong> ${lesson.introduction || ''}</p>
            <p><strong>Materials:</strong> ${lesson.materials || ''}</p>
            <p><strong>Homework:</strong> ${lesson.homework || ''}</p>
          </div>
        `}
        <div class="footer">
          <p>🌴 Penama Education Portal | Generated ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printAssessment = (withAnswers = false) => {
    if (!generatedAssessment) return;
    
    const printWindow = window.open('', '_blank');
    const subjectLabel = subjectOptions.find(s => s.value === selectedSubject)?.label || selectedSubject;
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${generatedAssessment.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { margin: 0 0 10px 0; }
          .info-row { display: flex; gap: 20px; margin-bottom: 10px; }
          .info-item { font-size: 14px; }
          .instructions { background: #f3f4f6; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
          .question { margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #eee; }
          .question-num { font-weight: bold; }
          .options { margin-left: 20px; margin-top: 10px; }
          .option { margin: 5px 0; }
          .answer-section { background: #d1fae5; padding: 15px; border-radius: 8px; margin-top: 30px; page-break-before: always; }
          .answer-section h2 { color: #065f46; margin-top: 0; }
          .answer-item { margin: 10px 0; padding: 10px; background: white; border-radius: 5px; }
          .matching-table { width: 100%; margin-top: 10px; }
          .matching-table td { width: 50%; vertical-align: top; }
          .blank { display: inline-block; min-width: 100px; border-bottom: 1px solid #333; }
          .footer { text-align: center; margin-top: 30px; padding: 15px; border-top: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${generatedAssessment.title}</h1>
          <div class="info-row">
            <span class="info-item"><strong>Subject:</strong> ${subjectLabel}</span>
            <span class="info-item"><strong>Year:</strong> ${selectedYear}</span>
            <span class="info-item"><strong>Time:</strong> ${generatedAssessment.time_limit} minutes</span>
            <span class="info-item"><strong>Total Marks:</strong> ${generatedAssessment.total_marks}</span>
          </div>
          <div class="info-row">
            <span class="info-item"><strong>Name:</strong> _______________________</span>
            <span class="info-item"><strong>Date:</strong> _____________</span>
          </div>
        </div>
        
        ${generatedAssessment.instructions ? `<div class="instructions"><strong>Instructions:</strong> ${generatedAssessment.instructions}</div>` : ''}
        
        ${generatedAssessment.questions?.map((q, i) => `
          <div class="question">
            <p><span class="question-num">Question ${q.number || i + 1}.</span> [${q.marks || 1} mark${(q.marks || 1) > 1 ? 's' : ''}]</p>
            <p>${q.question?.replace(/_{3,}/g, '<span class="blank"></span>') || ''}</p>
            ${q.type === 'multiple_choice' && q.options ? `
              <div class="options">
                ${q.options.map(opt => `<div class="option">○ ${opt}</div>`).join('')}
              </div>
            ` : ''}
            ${q.type === 'matching' && q.column_a && q.column_b ? `
              <table class="matching-table">
                <tr>
                  <td><strong>Column A</strong><br/>${q.column_a.map((item, idx) => `${idx + 1}. ${item}`).join('<br/>')}</td>
                  <td><strong>Column B</strong><br/>${q.column_b.map((item, idx) => `${String.fromCharCode(65 + idx)}. ${item}`).join('<br/>')}</td>
                </tr>
              </table>
              <p style="margin-top:10px">Answers: ${q.column_a.map((_, idx) => `${idx + 1}. ____`).join('  ')}</p>
            ` : ''}
            ${q.type === 'true_false' ? `
              <div class="options">
                <div class="option">○ True</div>
                <div class="option">○ False</div>
              </div>
            ` : ''}
            ${q.type === 'short_answer' ? `
              <p>_______________________________________________</p>
              <p>_______________________________________________</p>
            ` : ''}
          </div>
        `).join('')}
        
        ${withAnswers && generatedAssessment.answer_key ? `
          <div class="answer-section">
            <h2>📋 Answer Key (Teacher Copy)</h2>
            ${generatedAssessment.answer_key.map(a => `
              <div class="answer-item">
                <strong>Q${a.number}:</strong> ${a.answer}
                ${a.explanation ? `<br/><em style="color:#666">${a.explanation}</em>` : ''}
              </div>
            `).join('')}
          </div>
        ` : ''}
        
        <div class="footer">
          <p>🌴 Penama Education Portal | Generated ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <Card className="border-2 border-amber-200" data-testid="ai-lesson-generator">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-600" />
          AI Content Generator
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white ml-2">
            Powered by Gemini 3 Flash
          </Badge>
        </CardTitle>
        <CardDescription>
          Generate lessons, assessments, and more using AI
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="generate" className="flex items-center gap-1">
              <Wand2 className="w-4 h-4" />
              Lessons
            </TabsTrigger>
            <TabsTrigger value="assessment" className="flex items-center gap-1">
              <ClipboardList className="w-4 h-4" />
              Assessments
            </TabsTrigger>
            <TabsTrigger value="exam" className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              Term Exam
            </TabsTrigger>
            <TabsTrigger value="library" className="flex items-center gap-1">
              <FolderOpen className="w-4 h-4" />
              My Library
            </TabsTrigger>
          </TabsList>

          {/* LESSONS TAB */}
          <TabsContent value="generate" className="space-y-4">
            {/* Basic Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <Label>Topic / Lesson Focus *</Label>
                <Input
                  placeholder="e.g., Adding fractions, Life cycle of butterflies"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="mt-1"
                  data-testid="ai-topic-input"
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year Level</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear} defaultValue={yearOptions[0].toString()}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder={`Year ${yearOptions[0]}`} /></SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(y => (
                      <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-32">
                <Label>Duration</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 28}, (_, i) => 15 + i * 5).map(min => (
                      <SelectItem key={min} value={String(min)}>{min} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="ghost" className="mt-6 text-amber-700" onClick={() => setShowAdvanced(!showAdvanced)}>
                {showAdvanced ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                {showAdvanced ? 'Hide' : 'Show'} Advanced
              </Button>
            </div>

            {showAdvanced && (
              <div className="bg-amber-50 rounded-lg p-4 space-y-3 border border-amber-200">
                <h4 className="font-medium text-amber-800 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> Advanced Options
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Student Abilities</Label>
                    <Select value={studentAbilities} onValueChange={setStudentAbilities}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mixed">Mixed Abilities</SelectItem>
                        <SelectItem value="advanced">Advanced Learners</SelectItem>
                        <SelectItem value="struggling">Struggling Learners</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Teaching Style</Label>
                    <Select value={teachingStyle} onValueChange={setTeachingStyle}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="interactive">Interactive</SelectItem>
                        <SelectItem value="traditional">Traditional</SelectItem>
                        <SelectItem value="hands-on">Hands-on</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Learning Objectives (one per line)</Label>
                  <Textarea
                    placeholder="Enter specific objectives..."
                    value={learningObjectives}
                    onChange={(e) => setLearningObjectives(e.target.value)}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Generate Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={generateSingleLesson}
                disabled={loading || generatingWeek || !topic.trim()}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                data-testid="generate-ai-lesson-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Wand2 className="w-4 h-4 mr-2" />Generate Lesson</>}
              </Button>
              <Button
                onClick={generateWeekPlan}
                disabled={loading || generatingWeek || !topic.trim()}
                variant="outline"
                className="border-amber-300 text-amber-700"
                data-testid="generate-week-btn"
              >
                {generatingWeek ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Week (5 Days)</>}
              </Button>
            </div>

            {/* Generated Lesson Result */}
            {generatedLesson && (
              <div className="border-2 border-green-200 rounded-lg overflow-hidden">
                <div className="bg-green-50 p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Lesson Generated!
                    </h4>
                    <p className="text-sm text-green-600">{generatedLesson.title}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={differentiateLesson} 
                      disabled={differentiating}
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      {differentiating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Users className="w-4 h-4 mr-1" />Differentiate</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => saveLesson(generatedLesson)} disabled={savingLesson}>
                      {savingLesson ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                    </Button>
                    <Button size="sm" onClick={() => printLesson(generatedLesson)} className="bg-green-600 hover:bg-green-700">
                      <Printer className="w-4 h-4 mr-1" />Print
                    </Button>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <span className="font-medium text-gray-700">Objectives:</span>
                    <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                      {generatedLesson.objectives?.map((obj, i) => <li key={i}>{obj}</li>)}
                    </ul>
                  </div>
                  <div className="bg-amber-50 p-3 rounded">
                    <span className="font-medium text-amber-800">Introduction:</span>
                    <p className="text-sm text-gray-700 mt-1 italic">{generatedLesson.introduction}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="font-medium">Materials:</span> {generatedLesson.materials}</div>
                    <div><span className="font-medium">Homework:</span> {generatedLesson.homework}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Differentiated Lessons */}
            {differentiatedLessons && (
              <div className="border-2 border-purple-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4">
                  <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                    <Users className="w-5 h-5" /> 3 Differentiated Versions Created!
                  </h4>
                  <p className="text-sm text-purple-600 mt-1">
                    Choose a level to view and print. Each version is tailored for different learner needs.
                  </p>
                </div>
                
                {/* Level Selector */}
                <div className="p-4 bg-gray-50 border-b flex gap-2">
                  {[
                    { key: 'basic', label: 'Basic', icon: '🌱', color: 'bg-blue-100 border-blue-300 text-blue-800', desc: 'Extra support' },
                    { key: 'standard', label: 'Standard', icon: '📚', color: 'bg-green-100 border-green-300 text-green-800', desc: 'Most students' },
                    { key: 'advanced', label: 'Advanced', icon: '🚀', color: 'bg-orange-100 border-orange-300 text-orange-800', desc: 'Extension' }
                  ].map(level => (
                    <button
                      key={level.key}
                      onClick={() => setSelectedDiffLevel(level.key)}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        selectedDiffLevel === level.key 
                          ? level.color + ' shadow-md' 
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-lg">{level.icon}</div>
                      <div className="font-semibold">{level.label}</div>
                      <div className="text-xs opacity-75">{level.desc}</div>
                    </button>
                  ))}
                </div>
                
                {/* Selected Level Content */}
                <div className="p-4">
                  {differentiatedLessons[selectedDiffLevel] && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h5 className="font-semibold text-lg">{differentiatedLessons[selectedDiffLevel].title}</h5>
                          <p className="text-sm text-gray-500">{differentiatedLessons[selectedDiffLevel].level_description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => {
                            const lesson = differentiatedLessons[selectedDiffLevel];
                            saveLesson({ ...lesson, title: `[${selectedDiffLevel.toUpperCase()}] ${lesson.title}` });
                          }}>
                            <Save className="w-4 h-4 mr-1" />Save
                          </Button>
                          <Button size="sm" onClick={() => {
                            const lesson = differentiatedLessons[selectedDiffLevel];
                            printLesson({ ...lesson, objectives: lesson.objectives || [], activities: lesson.activities || [] });
                          }} className="bg-purple-600 hover:bg-purple-700">
                            <Printer className="w-4 h-4 mr-1" />Print
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <span className="font-medium text-gray-700">Objectives:</span>
                        <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                          {differentiatedLessons[selectedDiffLevel].objectives?.map((obj, i) => <li key={i}>{obj}</li>)}
                        </ul>
                      </div>
                      
                      <div className={`p-3 rounded ${
                        selectedDiffLevel === 'basic' ? 'bg-blue-50' : 
                        selectedDiffLevel === 'advanced' ? 'bg-orange-50' : 'bg-green-50'
                      }`}>
                        <span className="font-medium">Introduction:</span>
                        <p className="text-sm mt-1 italic">{differentiatedLessons[selectedDiffLevel].introduction}</p>
                      </div>
                      
                      {/* Activities */}
                      <div>
                        <span className="font-medium text-gray-700">Activities:</span>
                        <div className="mt-2 space-y-2">
                          {differentiatedLessons[selectedDiffLevel].activities?.map((act, i) => (
                            <div key={i} className="p-2 bg-gray-50 rounded text-sm">
                              <div className="flex justify-between">
                                <span className="font-medium">{act.name}</span>
                                <span className="text-gray-500">{act.duration}</span>
                              </div>
                              <p className="text-gray-600 mt-1">{act.description}</p>
                              {act.support_notes && (
                                <p className="text-blue-600 text-xs mt-1">💡 Support: {act.support_notes}</p>
                              )}
                              {act.extension_notes && (
                                <p className="text-orange-600 text-xs mt-1">🚀 Extension: {act.extension_notes}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Materials:</span> {differentiatedLessons[selectedDiffLevel].materials}</div>
                        <div><span className="font-medium">Homework:</span> {differentiatedLessons[selectedDiffLevel].homework}</div>
                      </div>
                      
                      {/* Extra tips for teachers */}
                      {differentiatedLessons[selectedDiffLevel].teacher_tips && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <span className="font-medium text-blue-800">💡 Teacher Tips:</span>
                          <p className="text-sm text-blue-700 mt-1">{differentiatedLessons[selectedDiffLevel].teacher_tips}</p>
                        </div>
                      )}
                      {differentiatedLessons[selectedDiffLevel].extension_ideas && (
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <span className="font-medium text-orange-800">🚀 Extension Ideas:</span>
                          <p className="text-sm text-orange-700 mt-1">{differentiatedLessons[selectedDiffLevel].extension_ideas}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Week Plan Result */}
            {weekLessons && (
              <div className="border-2 border-purple-200 rounded-lg overflow-hidden">
                <div className="bg-purple-50 p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-purple-800">📅 Week Plan Generated!</h4>
                    <p className="text-sm text-purple-600">5 connected lessons for "{topic}"</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => saveLesson(weekLessons[0], true)} disabled={savingLesson}>
                      {savingLesson ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                    </Button>
                    <Button size="sm" onClick={() => printLesson(null, true)} className="bg-purple-600 hover:bg-purple-700">
                      <Printer className="w-4 h-4 mr-1" />Print All
                    </Button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-5 gap-2">
                    {weekLessons.map((lesson, i) => (
                      <div key={i} className="bg-purple-50 p-3 rounded-lg text-center border border-purple-100">
                        <div className="font-bold text-purple-700 text-sm">{lesson.day}</div>
                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">{lesson.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ASSESSMENTS TAB */}
          <TabsContent value="assessment" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <Label>Assessment Topic *</Label>
                <Input
                  placeholder="e.g., Fractions, Plant biology, Computer hardware"
                  value={assessTopic}
                  onChange={(e) => setAssessTopic(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Assessment Type</Label>
                <Select value={assessType} onValueChange={setAssessType}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quiz">Quiz</SelectItem>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="exam">Exam</SelectItem>
                    <SelectItem value="worksheet">Worksheet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Number of Questions</Label>
                <Select value={numQuestions} onValueChange={setNumQuestions}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 questions</SelectItem>
                    <SelectItem value="10">10 questions</SelectItem>
                    <SelectItem value="15">15 questions</SelectItem>
                    <SelectItem value="20">20 questions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Question Types</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {questionTypeOptions.map(qt => (
                    <Badge
                      key={qt.value}
                      variant={questionTypes.includes(qt.value) ? "default" : "outline"}
                      className={`cursor-pointer ${questionTypes.includes(qt.value) ? 'bg-amber-500' : ''}`}
                      onClick={() => toggleQuestionType(qt.value)}
                    >
                      {qt.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={generateAssessment}
              disabled={generatingAssessment || !assessTopic.trim()}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
            >
              {generatingAssessment ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><ClipboardList className="w-4 h-4 mr-2" />Generate Assessment</>}
            </Button>

            {/* Generated Assessment */}
            {generatedAssessment && (
              <div className="border-2 border-blue-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-blue-800">{generatedAssessment.title}</h4>
                    <p className="text-sm text-blue-600">
                      {generatedAssessment.questions?.length} questions | {generatedAssessment.total_marks} marks | {generatedAssessment.time_limit} min
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={saveAssessment} disabled={savingAssessment}>
                      {savingAssessment ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => printAssessment(false)}>
                      <Printer className="w-4 h-4 mr-1" />Student
                    </Button>
                    <Button size="sm" onClick={() => printAssessment(true)} className="bg-blue-600 hover:bg-blue-700">
                      <Printer className="w-4 h-4 mr-1" />+ Answers
                    </Button>
                  </div>
                </div>
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {generatedAssessment.questions?.map((q, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between">
                        <span className="font-medium">Q{q.number || i + 1}. {q.question}</span>
                        <Badge variant="outline">{q.marks} mark{q.marks > 1 ? 's' : ''}</Badge>
                      </div>
                      {q.options && (
                        <div className="mt-2 ml-4 text-sm text-gray-600">
                          {q.options.map((opt, j) => <div key={j}>• {opt}</div>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Toggle Answer Key */}
                <div className="border-t p-3">
                  <Button variant="ghost" size="sm" onClick={() => setShowAnswerKey(!showAnswerKey)}>
                    <Eye className="w-4 h-4 mr-1" />
                    {showAnswerKey ? 'Hide' : 'Show'} Answer Key
                  </Button>
                  {showAnswerKey && generatedAssessment.answer_key && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg">
                      <h5 className="font-medium text-green-800 mb-2">Answer Key</h5>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {generatedAssessment.answer_key.map((a, i) => (
                          <div key={i} className="p-2 bg-white rounded">
                            <strong>Q{a.number}:</strong> {a.answer}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* TERM EXAM TAB */}
          <TabsContent value="exam" className="space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5" />
                End-of-Term Examination Generator
              </h3>
              <p className="text-sm text-purple-600">
                Generate comprehensive term exams with answer keys and mark sheets for easy batch grading.
              </p>
            </div>

            {/* Exam Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Subject</Label>
                <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map(sub => (
                      <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year Level</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(y => (
                      <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Term</Label>
                <Select value={examTerm} onValueChange={setExamTerm}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Term 1</SelectItem>
                    <SelectItem value="2">Term 2</SelectItem>
                    <SelectItem value="3">Term 3</SelectItem>
                    <SelectItem value="4">Term 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Total Marks</Label>
                <Select value={examTotalMarks} onValueChange={setExamTotalMarks}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50 marks</SelectItem>
                    <SelectItem value="60">60 marks</SelectItem>
                    <SelectItem value="80">80 marks</SelectItem>
                    <SelectItem value="100">100 marks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Duration (minutes)</Label>
                <Select value={examDuration} onValueChange={setExamDuration}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="150">2.5 hours</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Topics to Cover *</Label>
              <Textarea
                placeholder="Enter the main topics to be covered in this exam, separated by commas or new lines. E.g.:
- Atomic Structure and Periodic Table
- Chemical Bonding
- Stoichiometry
- Acids and Bases"
                value={examTopics}
                onChange={(e) => setExamTopics(e.target.value)}
                className="mt-1 min-h-[100px]"
              />
              <p className="text-xs text-gray-500 mt-1">
                List all topics from the term that should be assessed
              </p>
            </div>

            <Button 
              onClick={generateExam}
              disabled={generatingExam || !examTopics.trim()}
              className={`w-full ${!examTopics.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}
              data-testid="generate-exam-btn"
            >
              {generatingExam ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Exam Paper... (may take 1-2 minutes)
                </>
              ) : !examTopics.trim() ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Enter Topics Above to Generate Exam
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Term {examTerm} Exam
                </>
              )}
            </Button>
            
            {generatingExam && (
              <p className="text-sm text-center text-purple-600 mt-2 animate-pulse">
                AI is creating a comprehensive exam with multiple sections, answer key, and marking guide...
              </p>
            )}

            {/* Generated Exam Preview */}
            {generatedExam && (
              <div className="border-2 border-purple-200 rounded-lg overflow-hidden mt-4">
                <div className="bg-purple-50 p-4 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="font-semibold text-purple-800">
                      ✅ Term {examTerm} Examination Generated!
                    </h4>
                    <p className="text-sm text-purple-600">
                      Year {selectedYear} | {generatedExam.total_marks} marks | {generatedExam.duration_minutes} mins
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowExamAnswerKey(!showExamAnswerKey)}
                    >
                      {showExamAnswerKey ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {showExamAnswerKey ? 'Hide' : 'Show'} Answers
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => printExam(false)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print Exam
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => printExam(true)}
                      className="text-green-600 hover:text-green-700"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print with Answers
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => saveExamToGradeBook()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <ClipboardCheck className="w-4 h-4 mr-1" />
                      Save to Grade Book
                    </Button>
                  </div>
                </div>

                <div className="p-4">
                  <h5 className="font-medium text-gray-800 mb-3">Exam Structure:</h5>
                  <div className="space-y-3">
                    {generatedExam.sections?.map((section, sIdx) => (
                      <div key={sIdx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">
                            Section {String.fromCharCode(65 + sIdx)}: {section.title}
                          </span>
                          <Badge className="bg-purple-100 text-purple-700">
                            {section.marks} marks
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">
                          {section.questions?.length || 0} questions
                        </p>
                        
                        {showExamAnswerKey && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            {section.questions?.map((q, qIdx) => (
                              <div key={qIdx} className="text-sm py-1 flex gap-2">
                                <span className="text-gray-500">Q{qIdx + 1}:</span>
                                <span className="text-green-700 font-medium">{q.answer}</span>
                                <span className="text-gray-400">[{q.marks}m]</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <h5 className="font-medium text-yellow-800 flex items-center gap-2 mb-2">
                      📊 Mark Sheet for Batch Grading
                    </h5>
                    <p className="text-sm text-yellow-700">
                      The printable exam includes a mark sheet at the end where you can record marks for each question.
                      Use this for quick batch entry of raw marks.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* MY LIBRARY TAB */}
          <TabsContent value="library" className="space-y-4">
            {loadingLibrary ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : (
              <>
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4" /> Saved Lessons ({myLessons.length})
                  </h3>
                  {myLessons.length === 0 ? (
                    <p className="text-gray-500 text-sm">No saved lessons yet. Generate and save lessons to see them here.</p>
                  ) : (
                    <div className="grid gap-2">
                      {myLessons.map(lesson => (
                        <div 
                          key={lesson.id} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors border border-transparent hover:border-purple-200"
                          onClick={() => {
                            // View the lesson content
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(`
                              <!DOCTYPE html>
                              <html>
                              <head>
                                <title>${lesson.title}</title>
                                <style>
                                  body { font-family: Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto; }
                                  .header { border-bottom: 3px solid #7c3aed; padding-bottom: 15px; margin-bottom: 20px; }
                                  .header h1 { color: #7c3aed; margin: 0; }
                                  .meta { display: flex; gap: 10px; margin-top: 10px; }
                                  .badge { background: #f3f4f6; padding: 4px 12px; border-radius: 20px; font-size: 12px; }
                                  .content { line-height: 1.8; white-space: pre-wrap; }
                                  .btn { background: #7c3aed; color: white; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; margin-right: 10px; }
                                  @media print { .no-print { display: none; } }
                                </style>
                              </head>
                              <body>
                                <div class="no-print" style="margin-bottom: 20px;">
                                  <button class="btn" onclick="window.print()">🖨️ Print</button>
                                  <button class="btn" style="background:#666;" onclick="window.close()">✕ Close</button>
                                </div>
                                <div class="header">
                                  <h1>📚 ${lesson.title}</h1>
                                  <div class="meta">
                                    <span class="badge">${lesson.subject}</span>
                                    <span class="badge">Year ${lesson.year_level}</span>
                                    ${lesson.is_week_plan ? '<span class="badge" style="background:#ede9fe;color:#7c3aed;">Week Plan</span>' : ''}
                                  </div>
                                </div>
                                <div class="content">${lesson.content ? lesson.content.replace(/\n/g, '<br>') : 'No content available'}</div>
                                <div style="margin-top: 30px; text-align: center; color: #666; font-size: 12px;">
                                  <p>Generated by Penama Education Portal</p>
                                </div>
                              </body>
                              </html>
                            `);
                            printWindow.document.close();
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <Eye className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="font-medium">{lesson.title}</p>
                              <p className="text-sm text-gray-500">
                                Year {lesson.year_level} • {lesson.subject} • {lesson.is_week_plan ? '📅 Week Plan' : '📝 Single'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{new Date(lesson.created_at).toLocaleDateString()}</Badge>
                            <span className="text-xs text-purple-500">Click to open</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <ClipboardList className="w-4 h-4" /> Saved Assessments ({myAssessments.length})
                  </h3>
                  {myAssessments.length === 0 ? (
                    <p className="text-gray-500 text-sm">No saved assessments yet.</p>
                  ) : (
                    <div className="grid gap-2">
                      {myAssessments.map(assessment => (
                        <div 
                          key={assessment.id} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors border border-transparent hover:border-blue-200"
                          onClick={() => loadAssessmentFromLibrary(assessment.id)}
                          data-testid={`library-assessment-${assessment.id}`}
                        >
                          <div className="flex items-center gap-3">
                            {loadingAssessmentId === assessment.id ? (
                              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                            ) : (
                              <Eye className="w-5 h-5 text-gray-400" />
                            )}
                            <div>
                              <p className="font-medium">{assessment.title}</p>
                              <p className="text-sm text-gray-500">
                                Year {assessment.year_level} • {assessment.subject} • {assessment.num_questions} questions • {assessment.difficulty}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{new Date(assessment.created_at).toLocaleDateString()}</Badge>
                            <span className="text-xs text-blue-500">Click to open</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AILessonPlanGenerator;
